import moment from 'moment';
import { use } from 'react';

import { ClubCmsContent } from 'src/content/club-content-consumer';
import { SiteContext } from 'src/context/site-context';
import { useIsClient } from 'src/custom-hooks/use-client-snapshot';
import type { LanguageIsoCode } from 'src/enums/language';
import {
  DEFAULT_TRANSLATION_LANGUAGE_CODE,
  obtainTranslationDataForLanguage,
} from 'src/helpers/combine-translation-data';

const getTimedPromotionForClub = (
  siteId: string,
  promotions: Record<LanguageIsoCode, Queries.NewClubPromotion>[]
) => {
  const now = moment();

  return promotions?.find((promotion) => {
    const timedPromotion = promotion[DEFAULT_TRANSLATION_LANGUAGE_CODE];
    const isPromotionForCurrentClub = timedPromotion.siteIds?.includes(siteId);
    const isActive =
      moment(timedPromotion.promotionDetails?.to).isAfter(now) &&
      moment(timedPromotion.promotionDetails?.from).isBefore(now);

    return isActive && isPromotionForCurrentClub;
  });
};

export const useNewTimedPromotionForClub = () => {
  const cmsContent = use(ClubCmsContent);
  const { siteId, language, isGlobalPage } = use(SiteContext);

  const computeLivePromotionContent = (): Queries.NewClubPromotion | undefined => {
    if (isGlobalPage || !cmsContent?.newPromotionsContent) {
      return undefined;
    }

    const activeTimedPromotionForClub = getTimedPromotionForClub(
      siteId,
      cmsContent.newPromotionsContent
    );

    if (!activeTimedPromotionForClub) {
      return undefined;
    }

    return obtainTranslationDataForLanguage(activeTimedPromotionForClub, language);
  };

  const hasHydrated = useIsClient();
  // The timed promotion might become inactive between static generation and client-side hydration, so don't render it
  // initially.
  const isReady = isGlobalPage || hasHydrated;
  const promotionContent = hasHydrated ? computeLivePromotionContent() : undefined;

  const shouldUsePopupPromotion = promotionContent?.promotionDetails?.popup?.applyPromotionContent;
  const shouldUsePromoBlockPromotion =
    promotionContent?.promotionDetails?.promoBlockWithImage?.applyPromotionContent;
  const shouldUseEnquiryPromotion =
    promotionContent?.promotionDetails?.enquire?.applyPromotionContent;
  const shouldUseHeaderRibbonPromotion =
    promotionContent?.promotionDetails?.headerPromoRibbon?.applyPromotionContent;

  return {
    isTimedPromotionReady: isReady,
    timedPromoActiveForCurrentClub: promotionContent,
    shouldUsePopupPromotion,
    shouldUsePromoBlockPromotion,
    shouldUseEnquiryPromotion,
    shouldUseHeaderRibbonPromotion,
  };
};
